import React from "react";
import { useTranslation } from "react-i18next";
import {
  SecondaryCriteriaTemplate,
  SecondaryCriteria,
  ProjectElementDetailsConfiguration,
} from "@planzone/dto";
import {
  useRetrieveCurrentPlanzone,
  useRetrieveSecondaryCriteriaTemplate,
} from "@planzone/view-models";
import { DragAndDrop, Draggable, DropBox } from "@common/dragAndDrop";
import { CrossIcon } from "@common/crossIcon";
import { Props } from "./types";
import { ColumnIcon } from "./columnIcon";
import { Form } from "./form";
import styles from "./styles.scss";

function hasAvailableOptions(
  template: SecondaryCriteriaTemplate,
  profile: ProjectElementDetailsConfiguration[number],
) {
  return ![...template.map(({ uuid }) => uuid)].every(
    (key) => !!profile.columns.find((column) => column.key === key),
  );
}

function Columns({ project, formData, edit }: Props) {
  const { t, i18n } = useTranslation();
  const { planzone } = useRetrieveCurrentPlanzone();
  const { template: planzoneTemplate } = useRetrieveSecondaryCriteriaTemplate(
    planzone.uuid,
    "projectElement",
  );
  const { template: projectTemplate } = useRetrieveSecondaryCriteriaTemplate(
    project.uuid,
    "projectElement",
  );
  const template = [
    ...planzoneTemplate.map((sc) => ({ ...sc, isLocal: false })),
    ...projectTemplate.map((sc) => ({ ...sc, isLocal: true })),
  ];
  const currentLang =
    ["fr", "en"].find((lang) => new RegExp(lang, "i").test(i18n.language)) ||
    "fr";
  const getName = (key: string, criteria?: SecondaryCriteria) =>
    criteria
      ? criteria.labels[currentLang as keyof typeof criteria.labels]
        ? criteria.labels[currentLang as keyof typeof criteria.labels]
        : criteria.name
      : t(`projectElementDetailsConfiguration.columnNames.${key}`, key);

  return (
    <DragAndDrop>
      <div className={styles.columns}>
        {formData.columns
          .filter(({ key }) =>
            template.map(({ uuid }) => uuid).find((k) => k === key),
          )
          .map(({ key }, index, keys) => {
            const criteria = template.find(({ uuid }) => uuid === key);

            return (
              <React.Fragment key={key}>
                <DropBox
                  acceptedTypes={["MOVE_COLUMN"]}
                  horizontalSlop={20}
                  verticalSlop={20}
                  drop={({ payload }) => {
                    const droppedIndex = keys.findIndex(
                      ({ key }) => key === payload,
                    );
                    const nextColumns = [
                      ...keys.slice(0, index),
                      keys[droppedIndex],
                      keys[index],
                      ...keys.slice(index + 1),
                    ];

                    edit({
                      ...formData,
                      columns: [
                        ...nextColumns.slice(
                          0,
                          droppedIndex + (index < droppedIndex ? 1 : 0),
                        ),
                        ...nextColumns.slice(
                          droppedIndex + (index < droppedIndex ? 2 : 1),
                        ),
                      ],
                    });
                  }}
                >
                  {({ payload }, canDrop) => {
                    const criteria = template.find(
                      ({ uuid }) => uuid === payload,
                    );

                    return (
                      canDrop && (
                        <div className={styles.column}>
                          <ColumnIcon
                            project={project}
                            criteriaKey={payload}
                            className={styles.columnIcon}
                          />
                          <p>
                            {getName(payload, criteria)}
                            {criteria &&
                            "isLocal" in criteria &&
                            criteria.isLocal
                              ? ` (${t("projectElementDetailsConfiguration.localCriteria")})`
                              : ""}
                          </p>
                        </div>
                      )
                    );
                  }}
                </DropBox>
                <Draggable
                  type="MOVE_COLUMN"
                  data={key}
                  className={styles.columnWrapper}
                  draggedClassName={styles.hidden}
                >
                  <div className={styles.column}>
                    <ColumnIcon
                      project={project}
                      criteriaKey={key}
                      className={styles.columnIcon}
                    />
                    <p>
                      {getName(key, criteria)}
                      {criteria && "isLocal" in criteria && criteria.isLocal
                        ? ` (${t("projectElementDetailsConfiguration.localCriteria")})`
                        : ""}
                    </p>
                    <div
                      className={styles.iconBox}
                      onClick={() =>
                        edit({
                          ...formData,
                          columns: [
                            ...keys.slice(0, index),
                            ...keys.slice(index + 1),
                          ],
                        })
                      }
                    >
                      <CrossIcon className={styles.icon} />
                    </div>
                  </div>
                </Draggable>
                <DropBox
                  acceptedTypes={["MOVE_COLUMN"]}
                  horizontalSlop={20}
                  verticalSlop={20}
                  drop={({ payload }) => {
                    const droppedIndex = keys.findIndex(
                      ({ key }) => key === payload,
                    );
                    const nextColumns = [
                      ...keys.slice(0, index),
                      keys[index],
                      keys[droppedIndex],
                      ...keys.slice(index + 1),
                    ];

                    edit({
                      ...formData,
                      columns: [
                        ...nextColumns.slice(
                          0,
                          droppedIndex + (index < droppedIndex ? 1 : 0),
                        ),
                        ...nextColumns.slice(
                          droppedIndex + (index < droppedIndex ? 2 : 1),
                        ),
                      ],
                    });
                  }}
                >
                  {({ payload }, canDrop) => {
                    const criteria = template.find(
                      ({ uuid }) => uuid === payload,
                    );

                    return (
                      canDrop && (
                        <div className={styles.column}>
                          <ColumnIcon
                            project={project}
                            criteriaKey={payload}
                            className={styles.columnIcon}
                          />
                          <p>
                            {getName(payload, criteria)}
                            {criteria &&
                            "isLocal" in criteria &&
                            criteria.isLocal
                              ? ` (${t("projectElementDetailsConfiguration.localCriteria")})`
                              : ""}
                          </p>
                        </div>
                      )
                    );
                  }}
                </DropBox>
              </React.Fragment>
            );
          })}
        {hasAvailableOptions(template, formData) && (
          <div className={styles.columnWrapper}>
            <Form
              lang={currentLang as "fr" | "en"}
              project={project}
              formData={formData}
              edit={edit}
            />
          </div>
        )}
      </div>
    </DragAndDrop>
  );
}

export { Columns };
